import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addPartnerOrg,
  createPartner,
  createPartnerUser,
  deletePartnerUser,
  downloadBillingReport,
  getPartnerById,
  getPartners,
  getPartnerPortfolioApiKeys,
  getPartnerUsers,
  getPartnersReport,
  mintPartnerPortfolioApiKey,
  removePartnerOrg,
  revokePartnerPortfolioApiKey,
  updatePartnerUser,
  PARTNERS_BY_ID_STATE_KEY,
  PARTNERS_LIST_KEY,
  PARTNER_USERS_KEY,
  PARTNER_API_KEYS_KEY,
} from '../partners';
import {
  PartnerCreatePayload,
  PartnerOrgCreatePayload,
  PartnerUserCreatePayload,
  PartnerUserUpdatePayload,
} from '@/types/partners';
import { V2_ADMIN_PREFIX } from '../api';
import { downloadBlob, getFilenameFromHeaders, useDownloadReportMutation } from './reports';
import { toaster } from '@/components/ui/toaster-instance';

export const usePartnerByIdQuery = (partnerId: string) => {
  return useQuery({
    queryKey: [PARTNERS_BY_ID_STATE_KEY, partnerId],
    queryFn: async () => {
      if (!partnerId) {
        return null;
      }
      const { data } = await getPartnerById(partnerId);
      return data;
    },
    enabled: !!partnerId,
  });
};

export const usePartnersQuery = (searchQuery?: string) => {
  return useQuery({
    queryKey: [PARTNERS_LIST_KEY, searchQuery ?? ''],
    queryFn: () => getPartners(searchQuery),
  });
};

export const useDownloadPartnersReport = () => {
  return useDownloadReportMutation(
    'Partner report',
    'partners-report',
    async () => {
      const response = await getPartnersReport();
      return response.data as Blob;
    },
    'csv'
  );
};

export const useCreatePartnerMutation = () => {
  return useMutation({
    mutationFn: (payload: PartnerCreatePayload) => createPartner(payload),
  });
};

export const usePartnerUsersQuery = (partnerId: string) => {
  return useQuery({
    queryKey: [PARTNER_USERS_KEY, partnerId],
    queryFn: () => getPartnerUsers(partnerId),
    enabled: !!partnerId,
  });
};

export const useCreatePartnerUserMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerUserCreatePayload) => createPartnerUser(partnerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_USERS_KEY, partnerId] });
    },
  });
};

export const useUpdatePartnerUserMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: PartnerUserUpdatePayload }) =>
      updatePartnerUser(partnerId, userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_USERS_KEY, partnerId] });
    },
  });
};

export const useDeletePartnerUserMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deletePartnerUser(partnerId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_USERS_KEY, partnerId] });
    },
  });
};

export const useAddPartnerOrgMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerOrgCreatePayload) => addPartnerOrg(partnerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['data-table', `${V2_ADMIN_PREFIX}/partner/${partnerId}/organizations`],
      });
    },
  });
};

export const useRemovePartnerOrgMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (organizationId: string) => removePartnerOrg(partnerId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['data-table', `${V2_ADMIN_PREFIX}/partner/${partnerId}/organizations`],
      });
    },
  });
};

export const usePartnerPortfolioApiKeysQuery = (partnerId: string, enabled = true) => {
  return useQuery({
    queryKey: [PARTNER_API_KEYS_KEY, partnerId],
    queryFn: () => getPartnerPortfolioApiKeys(partnerId),
    enabled: enabled && !!partnerId,
  });
};

export const useMintPartnerPortfolioApiKeyMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => mintPartnerPortfolioApiKey(partnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_API_KEYS_KEY, partnerId] });
    },
  });
};

export const useRevokePartnerPortfolioApiKeyMutation = (partnerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKeyId: string) => revokePartnerPortfolioApiKey(partnerId, apiKeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_API_KEYS_KEY, partnerId] });
    },
  });
};

export const useDownloadBillingReportMutation = () => {
  return useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) =>
      downloadBillingReport(startDate, endDate),
    onSuccess: result => {
      const filename = getFilenameFromHeaders(result.headers, 'billing-report');
      downloadBlob(result.data, filename);
      toaster.create({
        title: 'Success',
        description: 'Billing report downloaded successfully',
        type: 'success',
      });
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to download billing report. Please try again.',
        type: 'error',
      });
    },
  });
};
